import React, { useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Dialog, Grid, Typography } from '@mui/material';

import { useLanguage } from 'context/AppContext';

import { ModalContent } from '../DeliveryPortalPage.styles';

interface ConfirmOverwriteModalProps {
  onConfirm: () => Promise<void>;
  onCancel: () => Promise<void>;
  isShown: boolean;
  numDuplicates: number;
  numSetsNotFound: number;
  numInvalidGSOneCodes: number;
}

const ConfirmOverwriteModal: React.FC<ConfirmOverwriteModalProps> = ({
  onCancel,
  onConfirm,
  isShown,
  numDuplicates,
  numSetsNotFound,
  numInvalidGSOneCodes,
}) => {
  const [language] = useLanguage();
  const [modalState, setModalState] = useState<'idle' | 'cancelling' | 'confirming'>('idle');

  const handleConfirm = async () => {
    setModalState('confirming');
    await onConfirm();
    setModalState('idle');
  };

  const handleCancel = async () => {
    setModalState('cancelling');
    await onCancel();
    setModalState('idle');
  };

  return (
    <Dialog open={isShown} maxWidth="md">
      <ModalContent>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={11}>
            <Typography style={{ fontWeight: 'bold' }} align="center">
              {language.DeliveryPortal.ConfirmOverwriteModal.heading}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <CancelIcon color="error" fontSize="large" />
          </Grid>
        </Grid>
        <Box mb={4} />
        {numDuplicates > 0 ? (
          <Typography align="center">{language.DeliveryPortal.ConfirmOverwriteModal.subheading}</Typography>
        ) : null}
        <Box mb={4} />
        {numSetsNotFound > 0 ? (
          <Typography align="center">
            {language.DeliveryPortal.ConfirmOverwriteModal.setsNotFound.replace(
              '{{notFoundCount}}',
              numSetsNotFound.toString(),
            )}
          </Typography>
        ) : null}
        <Box mb={4} />
        {numInvalidGSOneCodes > 0 ? (
          <Typography align="center">
            {language.DeliveryPortal.ConfirmOverwriteModal.validGSOneCode.replace(
              '{{numInvalidGSOneCodes}}',
              numInvalidGSOneCodes.toString(),
            )}
          </Typography>
        ) : null}
        <Box sx={{ mb: 4 }} />
        <Grid justifyContent="space-between" container spacing={2}>
          <Grid item>
            <LoadingButton
              loading={modalState === 'cancelling'}
              variant="contained"
              startIcon={<DeleteForeverIcon />}
              onClick={handleCancel}
            >
              {language.DeliveryPortal.ConfirmOverwriteModal.cancelButton}
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton
              loading={modalState === 'confirming'}
              variant="contained"
              startIcon={<FolderSpecialIcon />}
              onClick={handleConfirm}
            >
              {language.DeliveryPortal.ConfirmOverwriteModal.confirmButton}
            </LoadingButton>
          </Grid>
        </Grid>
      </ModalContent>
    </Dialog>
  );
};

export default ConfirmOverwriteModal;
